import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { makeAutoObservable } from 'mobx';
import * as API from '@/API';
import NomenclatureOverviewModel from '@/Models/NomenclatureOverviewModel';
import { SortDirection } from '@tanstack/react-table';

interface StateData {
    page: number;
    amountToShow: number;
    totalAmount: number;
    filter: string;
    nomenclatures: NomenclatureOverviewModel[];
    sortColumn: string;
    sortOrder: SortDirection;
}

interface ErrorResponse {
    Error: boolean;
    Message: string;
}

export default class NomenclaturesOverviewStore {
    state: StateData = {
        page: 0,
        amountToShow: 20,
        totalAmount: 0,
        filter: '',
        nomenclatures: [],
        sortColumn: 'Number',
        sortOrder: 'asc',
    };

    loading = true;
    deleteId = -1;
    showDialog = false;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>) {
        this.fcStore = fcStore;
        makeAutoObservable(this);

        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    private readonly initOld = (state: StateData): void => {
        this.loading = false;
        this.setState({
            page: state.page,
            amountToShow: state.amountToShow,
            totalAmount: state.totalAmount,
            filter: state.filter,
            nomenclatures: state.nomenclatures,
            sortColumn: state.sortColumn,
            sortOrder: state.sortOrder,
        });
        this.newSearch();
    };

    private readonly initNew = (): void => {
        this.search();
    };

    private readonly setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    setTitle = (val: string): void => {
        this.fcStore.updateTitle(val);
    };

    newSearch = (): void => {
        this.state.page = 0;
        this.search();
    };

    search = (): void => {
        this.setLoading(true);
        void API.nomenclatures.getOverview<{ data: NomenclatureOverviewModel[]; total: number }, ErrorResponse>({
            filter: this.state.filter,
            page: this.state.page,
            amountToShow: this.state.amountToShow,
            sortColumn: this.state.sortColumn,
            sortOrder: this.state.sortOrder,
        }, (res) => {
            this.setLoading(false);
            this.setNomenclatures(res.data);
            this.setTotalAmount(res.total);
            this.fcStore.updateState(this.state);
        }, (err) => {
            this.setLoading(false);
            console.error(err.Message);
        });
    };

    setFilter = (value: string): void => {
        this.state.filter = value;
        this.fcStore.updateState(this.state);
    };

    setPagination = (page: number): void => {
        this.state.page = page;
        this.search();
    };

    setSorting = (sorting: string, dir: SortDirection): void => {
        this.state.sortOrder = dir;
        this.state.sortColumn = sorting;
        this.fcStore.updateState(this.state);
        this.newSearch();
    };

    tryDelete = (id: number): void => {
        this.deleteId = id;
        this.showDialog = true;
    };

    cancelDelete = (): void => {
        this.deleteId = -1;
        this.showDialog = false;
    };

    delete = (): void => {
        if (this.deleteId === -1) {
            return;
        }

        this.showDialog = false;
        void API.nomenclatures.delete(this.deleteId)
            .then(() => {
                this.newSearch();
            });
    };

    private readonly setLoading = (loading: boolean): void => {
        this.loading = loading;
    };

    private readonly setNomenclatures = (nomenclatures: NomenclatureOverviewModel[]): void => {
        this.state.nomenclatures = nomenclatures;
    };

    private readonly setTotalAmount = (amount: number): void => {
        this.state.totalAmount = amount;
    };
}
